import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth.context";
import { useOrganization } from "@/context/organization.context";
import {
  ArrowRight,
  Building2,
  Landmark,
  LogOut,
  Mail,
  Plus,
  PlusCircle,
  Search,
  Settings,
  UserCog,
  Users,
} from "lucide-react";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import useDomain from "@/hooks/use-domain";
import authService from "@/services/auth.service";
import { useTranslation } from "react-i18next";
import { app_env, env_config } from "@/env-config";

export default function ChooseOrg() {
  const { t } = useTranslation("chooseOrg");
  const { setActiveOrg } = useOrganization();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { orgDomain } = useDomain(env_config[app_env].domain);
  const { activeOrg, organizations = [] } = useOrganization();

  const handleSelectOrganization = (orgId: string) => {
    setActiveOrg(orgId);
    navigate(`/choose-module`);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      logout();
      navigate("/login");
    } catch (error) {
      toast.error(t("errors.logoutFailed"));
    }
  };

  const filteredOrgs = organizations.filter(
    (org) =>
      org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      org.industry?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const getInitials = (name: string) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t("greeting.morning");
    if (hour < 18) return t("greeting.afternoon");
    return t("greeting.evening");
  };

  return (
    <>
      <div className="min-h-screen flex">
        {/* Left Panel - User Info & Branding */}
        <div className="hidden lg:flex lg:w-[440px] lg:fixed lg:inset-y-0 lg:left-0 bg-primary dark:bg-primaryDark flex-col p-8 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5 dark:opacity-[2%]">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:4rem_4rem]" />
          </div>

          {/* Content */}
          <div className="relative z-10 flex flex-col h-full">
            {/* Logo */}
            <div className="mb-10">
              <Link to={"/"}>
                <img className="h-7" src="logo_white.png" alt="" />
              </Link>
            </div>

            {/* User Profile Section */}
            <div className="mb-4">
              <p className="text-primary-foreground/60 text-sm font-medium uppercase tracking-wider mb-4">
                {t("loggedInAs")}
              </p>
              <div className="flex items-center gap-4">
                <Avatar className="h-12 w-12 border-[3px] border-white/30 ring-2 ring-white/10">
                  <AvatarImage src={user?.photo} />
                  <AvatarFallback className="bg-card text-primaryDark text-xl font-semibold">
                    {getInitials(user?.name || user?.email)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-base truncate">
                    {user?.name || "User"}
                  </h3>
                  <p className="text-white/70 text-sm truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-white/10 my-2"></div>

            {/* Action Links */}
            <div className="space-y-3 mt-4 flex-1">
              {/* Account Settings - Simple link style */}
              <button
                onClick={() => navigate("/account-settings")}
                className="w-full flex items-center gap-3 px-4 py-2.5  border border-slate-100/20  rounded-md text-white/80 hover:text-white hover:bg-card/5 transition-colors text-left group"
              >
                <UserCog className="h-4 w-4" />
                <span className="text-sm">{t("accountSettings")}</span>
              </button>

              {user?.isAdmin && !orgDomain && (
                <button
                  onClick={() => navigate("/admin")}
                  className="w-full border  border-slate-100/20  flex items-center gap-3 px-4 py-3.5 rounded-md text-white hover:bg-card/10 transition-colors text-left group"
                >
                  <div className="h-9 w-9 rounded-md bg-card/10 flex items-center justify-center group-hover:bg-card/20 transition-colors">
                    <Settings className="h-4 w-4" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="font-medium text-sm leading-4">
                      {t("adminDashboard")}
                    </span>
                    <span className="text-white/60 text-xs">
                      {t("manageSettings")}
                    </span>
                  </div>
                </button>
              )}

              <button
                onClick={handleLogout}
                className="w-full flex items-center border border-slate-100/20 gap-3 px-4 py-3.5 rounded-md text-white hover:bg-card/10 bg-card/5 transition-colors text-left group"
              >
                <div className="h-9 w-9 rounded-md bg-card/10 flex items-center justify-center group-hover:bg-card/20 transition-colors">
                  <LogOut className="h-4 w-4" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="font-medium text-sm leading-4">
                    {t("signOut")}
                  </span>
                  <span className="text-white/60 text-xs">
                    {t("endSession")}
                  </span>
                </div>
              </button>
            </div>

            {/* Footer */}
            <div className="pt-6 mt-auto">
              <p className="text-white/40 text-sm">
                {t("footerRights", { year: new Date().getFullYear() })}
              </p>
            </div>
          </div>
        </div>

        {/* Right Panel - Organization Selection */}
        <div className="flex-1 flex flex-col bg-backgroud lg:ml-[440px]">
          {/* Mobile Header */}
          <header className="flex items-center justify-between px-4 py-3 bg-card border-b border-border lg:hidden">
            <Link to="/" className="flex items-center gap-2">
              <img className="h-7" src="logo_white.png" alt="" />
            </Link>
            <div className="flex items-center gap-2">
              {/* <SimpleLanguageSwitcher /> */}
              {user?.role === "admin" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/admin")}
                  className="rounded-md"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="rounded-md text-muted-foreground"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </header>

          {/* Mobile User Info */}
          <div className="px-4 py-4 bg-card border-b border-border lg:hidden">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user?.photo} />
                <AvatarFallback className="bg-primary/10 text-primary font-medium">
                  {getInitials(user?.name || user?.email)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-muted-foreground text-xs">
                  {getGreeting()},
                </p>
                <p className="font-medium text-foreground truncate">
                  {user?.name || "User"}
                </p>
              </div>
              <Badge variant="outline" className="rounded-md text-xs">
                {t("orgCount", { count: organizations.length })}
              </Badge>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-3 md:p-6">
            <div className="max-w-xl mx-auto">
              {organizations.length > 0 ? (
                <>
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-foreground mb-1">
                      {t("workspaces.title")}
                    </h2>
                    <p className="text-muted-foreground text-sm">
                      {t("workspaces.subtitle")}
                    </p>
                  </div>

                  {/* Search */}
                  {organizations.length > 3 && (
                    <div className="relative mb-5">
                      <Search className="absolute z-40 left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foregroun" />
                      <Input
                        placeholder={t("search.placeholder")}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 rounded-md border-border bg-card h-11"
                      />
                    </div>
                  )}

                  {/* Organizations Grid */}
                  <div className="grid gap-3">
                    {filteredOrgs.map((org, index) => (
                      <button
                        key={org.id}
                        onClick={() => handleSelectOrganization(org.id)}
                        className="w-full p-3 rounded-md border border-border bg-card hover:border-primary hover:ring-2 hover:ring-primary/10 transition-all text-left group"
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className="flex h-10 w-10 items-center justify-center rounded-md text-white flex-shrink-0"
                            style={{
                              backgroundColor: [
                                "#6366f1",
                                "#8b5cf6",
                                "#ec4899",
                                "#f43f5e",
                                "#f97316",
                                "#eab308",
                                "#22c55e",
                                "#14b8a6",
                              ][index % 8],
                            }}
                          >
                            <Landmark className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-sm text-foreground truncate">
                                {org.name}
                              </h3>
                              <Badge className="text-xs px-2 py-0.5 rounded-md bg-primary/10 text-primary">
                                {org?.role}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                              {org.industry && (
                                <Badge
                                  variant="outline"
                                  className="capitalize text-muted-foreground"
                                >
                                  {org.industry}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-center h-8 w-8 rounded-md bg-accent group-hover:bg-primary group-hover:text-white transition-colors">
                            <ArrowRight className="h-4 w-4" />
                          </div>
                        </div>
                      </button>
                    ))}

                    {filteredOrgs.length === 0 && searchQuery && (
                      <div className="text-center py-12 bg-card rounded-md border border-border">
                        <Search className="h-8 z-50 w-8 mx-auto mb-3" />
                        <p className="text-base font-medium mb-1 text-foreground">
                          {t("search.noResults")}
                        </p>
                        <p className="text-muted-foreground text-sm">
                          {t("search.tryDifferent")}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Join Organization */}
                  <div className="mt-6">
                    <button
                      onClick={() => setIsOpen(true)}
                      className="w-full flex items-center justify-center gap-2 p-2.5 text-sm rounded-md border bg-card border-dashed border-gray-300 text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-all"
                    >
                      <PlusCircle className="h-4 w-4" />
                      <span className="font-medium">{t("joinAnother")}</span>
                    </button>
                  </div>
                </>
              ) : (
                /* Empty State */
                <div className="text-center py-16 px-6 bg-card rounded-md border border-border">
                  {orgDomain ? (
                    <>
                      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-md bg-primary/10">
                        <Building2 className="h-8 w-8 text-primary" />
                      </div>
                      <h2 className="text-lg font-semibold text-foreground mb-2">
                        {t("emptyState.welcomeTitle")}
                      </h2>
                      <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto">
                        {t("emptyState.welcomeSubtitle")}
                      </p>
                      <Button
                        onClick={() => navigate("/choose-module")}
                        className="rounded-md"
                        size="sm"
                      >
                        {t("emptyState.continueToAccount")}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </>
                  ) : activeOrg?.name ? (
                    <>
                      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-md bg-red-50">
                        <Building2 className="h-8 w-8 text-red-500" />
                      </div>
                      <h2 className="text-xl font-semibold text-foreground mb-2">
                        {t("emptyState.accessDeniedTitle")}
                      </h2>
                      <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                        {t("emptyState.accessDeniedSubtitle", {
                          orgName: activeOrg?.name,
                        })}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button
                          onClick={() => setIsOpen(true)}
                          className="rounded-md"
                        >
                          <Users className="mr-2 h-4 w-4" />
                          {t("emptyState.requestToJoin")}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() =>
                            window.open("https://one-suite.cloud/contact")
                          }
                          className="rounded-md"
                        >
                          <Mail className="mr-2 h-4 w-4" />
                          {t("emptyState.contactSupport")}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-md bg-accent/50">
                        <Building2 className="h-8 w-8 text-muted-foregroun" />
                      </div>
                      <h2 className="text-lg font-semibold text-foreground mb-2">
                        {t("emptyState.noOrgsTitle")}
                      </h2>
                      <p className="text-muted-foreground leading-6 text-sm mb-6 max-w-sm mx-auto">
                        {t("emptyState.noOrgsSubtitle")}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button
                          onClick={() => setIsOpen(true)}
                          className="rounded-md"
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          {t("emptyState.joinOrganization")}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => navigate("/contact")}
                          className="rounded-md"
                        >
                          {t("emptyState.contactSupport")}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Footer */}
          <footer className="px-4 py-3 border-t border-border bg-card lg:hidden">
            <p className="text-center text-muted-foregroun text-xs">
              {t("footerRights", { year: new Date().getFullYear() })}
            </p>
          </footer>
        </div>
      </div>

      <JoinOrganizationModal
        activeOrg={activeOrg}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </>
  );
}

function JoinOrganizationModal({ isOpen, setIsOpen, activeOrg }: any) {
  const { t } = useTranslation("chooseOrg");
  const [businessCode, setBusinessCode] = useState(
    activeOrg?.businessCode || "",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleJoinRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!businessCode.trim()) {
      setError(t("joinModal.required"));
      return;
    }

    setIsSubmitting(true);

    try {
      await api.post("/organizations/join", {
        businessCode,
      });

      setBusinessCode("");
      setIsOpen(false);
      toast.success(t("joinModal.success"));
    } catch (err: any) {
      setError(err?.response?.data?.message || t("joinModal.error"));
      toast.error(t("joinModal.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    if (!isSubmitting) {
      setIsOpen(false);
      setError("");
      setBusinessCode("");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleModalClose}>
      <DialogContent className="sm:max-w-md rounded-md">
        <DialogHeader className="text-center">
          <DialogTitle className="text-base font-semibold">
            {t("joinModal.title")}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {t("joinModal.subtitle")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleJoinRequest} className="space-y-4">
          <div className="space-y-2  px-4">
            <Label
              htmlFor="businessCode"
              className="text-sm font-medium text-muted-foreground"
            >
              {t("joinModal.businessCode")}
            </Label>
            <Input
              id="businessCode"
              type="text"
              placeholder={t("joinModal.placeholder")}
              value={businessCode}
              onChange={(e) => {
                setBusinessCode(e.target.value);
                setError("");
              }}
              disabled={isSubmitting}
              className="rounded-md"
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleModalClose}
              disabled={isSubmitting}
              className="rounded-md flex-1 sm:flex-none"
            >
              {t("joinModal.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !businessCode.trim()}
              className="rounded-md flex-1 sm:flex-none"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("joinModal.joining")}
                </>
              ) : (
                t("joinModal.join")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
